import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const REGIONS = ['US','Europe','Southeast Asia','Canada','ANZ','South Asia','Middle East','East Asia','Africa','Other']
const CONTACT_TYPES = ['Past Client','Lead','VIP','Trade Contact']

// All measurements in inches
const MEASUREMENT_SECTIONS = {
  general: {
    label: 'General',
    fields: [
      { key: 'height', label: 'Height', unit: 'in' },
      { key: 'weight', label: 'Weight', unit: 'lbs' },
    ]
  },
  suit_jacket: {
    label: 'Suit / Jacket',
    fields: [
      { key: 'chest', label: 'Chest' },
      { key: 'waist', label: 'Waist' },
      { key: 'hips', label: 'Hips / Seat' },
      { key: 'shoulders', label: 'Shoulders' },
      { key: 'back_length', label: 'Back Length' },
      { key: 'front_length', label: 'Front Length' },
      { key: 'sleeve_right', label: 'Sleeve (Right)' },
      { key: 'sleeve_left', label: 'Sleeve (Left)' },
      { key: 'bicep', label: 'Bicep' },
      { key: 'wrist', label: 'Wrist' },
      { key: 'neck', label: 'Neck' },
    ]
  },
  trouser: {
    label: 'Trouser',
    fields: [
      { key: 'trouser_waist', label: 'Trouser Waist' },
      { key: 'seat', label: 'Seat' },
      { key: 'thigh', label: 'Thigh' },
      { key: 'knee', label: 'Knee' },
      { key: 'calf', label: 'Calf' },
      { key: 'inseam', label: 'Inseam' },
      { key: 'outseam', label: 'Outseam' },
      { key: 'bottom_width', label: 'Bottom Width' },
      { key: 'rise_front', label: 'Rise (Front)' },
      { key: 'rise_back', label: 'Rise (Back)' },
    ]
  },
  shirt: {
    label: 'Shirt',
    fields: [
      { key: 'collar', label: 'Collar' },
      { key: 'shirt_chest', label: 'Chest' },
      { key: 'shirt_waist', label: 'Waist' },
      { key: 'shirt_length', label: 'Length' },
      { key: 'shirt_sleeve', label: 'Sleeve' },
    ]
  }
}

export default function NewClient() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('info')
  const [measurements, setMeasurements] = useState({})
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', whatsapp: '',
    company: '', job_title: '', country: '', city: '', region_bucket: '',
    contact_type: 'Past Client', trunk_show_market: '', referral_source: '',
    notes: '', website: '',
  })

  function setField(k, v) { setForm(p => ({ ...p, [k]: v })) }
  function setMeasure(k, v) { setMeasurements(p => ({ ...p, [k]: v })) }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)

    // Save client
    const { data: client, error: cErr } = await supabase.from('clients')
      .insert({ ...form, added_by: profile?.id })
      .select().single()

    if (cErr) { alert('Error: ' + cErr.message); setSaving(false); return }

    // Save measurements if any filled
    const hasAnyMeasurement = Object.values(measurements).some(v => v !== '' && v != null)
    if (hasAnyMeasurement) {
      const measurePayload = { client_id: client.id, garment_type: 'suit', unit: 'in', measured_by: profile?.id }
      Object.entries(measurements).forEach(([k, v]) => {
        if (v !== '' && v != null) measurePayload[k] = parseFloat(v)
      })
      await supabase.from('measurements').insert(measurePayload)
    }

    navigate(`/clients/${client.id}`)
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">New Client</h1>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>Info</button>
        <button className={`tab ${activeTab === 'measurements' ? 'active' : ''}`} onClick={() => setActiveTab('measurements')}>Measurements</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── INFO TAB ── */}
        {activeTab === 'info' && (
          <div className="fade-in">
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="form-section-title">Personal Details</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" value={form.first_name} onChange={e => setField('first_name', e.target.value)} placeholder="First name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" value={form.last_name} onChange={e => setField('last_name', e.target.value)} placeholder="Last name" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" type="tel" value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="+1 555 000 0000" />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp <span>(if different)</span></label>
                  <input className="form-input" type="tel" value={form.whatsapp} onChange={e => setField('whatsapp', e.target.value)} placeholder="+66 81 xxx xxxx" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input className="form-input" value={form.company} onChange={e => setField('company', e.target.value)} placeholder="Company name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Job Title</label>
                  <input className="form-input" value={form.job_title} onChange={e => setField('job_title', e.target.value)} placeholder="Job title" />
                </div>
              </div>
            </div>

            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="form-section-title">Location</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input className="form-input" value={form.country} onChange={e => setField('country', e.target.value)} placeholder="Country" />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" value={form.city} onChange={e => setField('city', e.target.value)} placeholder="City" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Region</label>
                  <select className="form-select" value={form.region_bucket} onChange={e => setField('region_bucket', e.target.value)}>
                    <option value="">Select region</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Type</label>
                  <select className="form-select" value={form.contact_type} onChange={e => setField('contact_type', e.target.value)}>
                    {CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="form-section-title">Additional</div>
              <div className="form-group">
                <label className="form-label">Referral Source</label>
                <input className="form-input" value={form.referral_source} onChange={e => setField('referral_source', e.target.value)} placeholder="Who referred this client?" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={3} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Any notes about this client…" />
              </div>
            </div>

            <button type="button" className="btn btn-primary btn-lg" style={{ borderRadius: '12px', marginBottom: '8px' }} onClick={() => setActiveTab('measurements')}>
              Next: Add Measurements →
            </button>
          </div>
        )}

        {/* ── MEASUREMENTS TAB ── */}
        {activeTab === 'measurements' && (
          <div className="fade-in">
            <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', fontSize: '13px', color: '#92400E' }}>
              All measurements in <strong>inches (in)</strong>. All fields optional — fill in what's available.
            </div>

            {Object.entries(MEASUREMENT_SECTIONS).map(([sectionKey, section]) => (
              <div className="card" key={sectionKey} style={{ marginBottom: '12px' }}>
                <div className="form-section-title">{section.label}</div>
                <div className="form-row">
                  {section.fields.map(field => (
                    <div className="form-group" key={field.key}>
                      <label className="form-label">
                        {field.label} <span>{field.unit || 'in'}</span>
                      </label>
                      <input
                        className="form-input"
                        type="number" step="0.25" min="0"
                        value={measurements[field.key] || ''}
                        onChange={e => setMeasure(field.key, e.target.value)}
                        placeholder="0.00"
                        inputMode="decimal"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="form-section-title">Fit Notes</div>
              <div className="form-group">
                <label className="form-label">Posture / Build Notes</label>
                <textarea className="form-textarea" rows={2}
                  value={measurements.posture_notes || ''}
                  onChange={e => setMeasure('posture_notes', e.target.value)}
                  placeholder="e.g. Slightly stooped, broad shoulders, high left shoulder…"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Fit Preferences</label>
                <textarea className="form-textarea" rows={2}
                  value={measurements.fit_notes || ''}
                  onChange={e => setMeasure('fit_notes', e.target.value)}
                  placeholder="e.g. Likes slim fit, extra room in thighs, longer jacket…"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ borderRadius: '12px', marginBottom: '8px' }}>
              {saving ? 'Saving…' : '✓ Save Client'}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
